import clsx from 'clsx';
import { useEffect, useState, type JSX } from 'react';
import { z } from 'zod';
import { PillButton } from '@/components/entity';
import { Form, FormActions, FormField, FormMessage } from '@/components/form';
import { useFormController } from '@/lib/forms/formController';
import { trackLeadSubmission } from '@/lib/analytics/events';
import { LEAD_CAMPAIGN_MEDIUM_CONTACT, LEAD_CAMPAIGN_SOURCE_CONTACT } from '@/lib/constants/leads';
import type { ContactPageFormContent } from '@/lib/types/siteSettings';
import { submitLeadFromLanding } from '@/lib/services/leads';
import { applyTemplate } from '@/lib/utils/textTemplates';

const CONTACT_FORM_SCHEMA = z.object({
  fullName: z.string().trim().min(1, 'Full name is required'),
  phone: z.string().trim().min(1, 'Phone number is required'),
  email: z.string().trim().email('Enter a valid email'),
  spaceType: z.string().trim().optional(),
  targetSize: z.string().trim().optional(),
  timeline: z.string().trim().optional(),
  message: z.string().trim().min(1, 'Message is required'),
});

type ContactFormValues = z.infer<typeof CONTACT_FORM_SCHEMA>;

const EMPTY_FORM: ContactFormValues = {
  fullName: '',
  phone: '',
  email: '',
  spaceType: '',
  targetSize: '',
  timeline: '',
  message: '',
};

const CONTACT_PROFILE_KEY = 'public_contact_profile';

export const DEFAULT_CONTACT_FORM_CONTENT: ContactPageFormContent = {
  title: 'Send us a message',
  description: 'Share your contact details and workspace needs. We respond within 1 business day.',
  successMessage: 'Thank you, {name}. We received your message and will be in touch shortly.',
  errorMessage: 'Unable to submit your message.',
  helperText: 'Prefer to talk? Call us at {phone}.',
  submitLabel: 'Send message',
  fields: {
    fullNameLabel: 'Full name',
    phoneLabel: 'Phone number',
    emailLabel: 'Email address',
    messageLabel: 'Message',
    fullNamePlaceholder: 'Jane Doe',
    phonePlaceholder: '{phone}',
    emailPlaceholder: 'you@example.com',
    messagePlaceholder: 'Tell us about your ideal workspace and timing.',
    spaceTypeLabel: 'Space type (optional)',
    spaceTypePlaceholder: 'Select a space type',
    spaceTypeOptions: ['Office', 'Retail', 'Industrial', 'Flex', 'Other'],
    targetSizeLabel: 'Target size in sq ft (optional)',
    targetSizePlaceholder: '2,500',
    timelineLabel: 'Move-in timeline (optional)',
    timelinePlaceholder: 'Select a timeline',
    timelineOptions: ['Immediately', '1-3 months', '3-6 months', '6+ months'],
  },
};

interface ContactLeadFormProps {
  content: ContactPageFormContent;
  contactPhone: string;
  className?: string;
}

function normalizeOptionalText(value: string | undefined | null): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function buildContactSubmissionNotes(values: ContactFormValues): string {
  const lines: string[] = [];
  const message = values.message.trim();
  if (message.length > 0) {
    lines.push(message);
  }

  const spaceType = normalizeOptionalText(values.spaceType);
  if (spaceType) {
    lines.push(`Space type: ${spaceType}`);
  }

  const targetSize = normalizeOptionalText(values.targetSize);
  if (targetSize) {
    lines.push(`Target size: ${targetSize}`);
  }

  const timeline = normalizeOptionalText(values.timeline);
  if (timeline) {
    lines.push(`Timeline: ${timeline}`);
  }

  return lines.join('\n');
}

function loadContactProfile(): Partial<ContactFormValues> {
  if (typeof window === 'undefined') {
    return {};
  }
  try {
    const stored = window.localStorage.getItem(CONTACT_PROFILE_KEY);
    if (!stored) {
      return {};
    }
    const parsed = JSON.parse(stored) as Partial<ContactFormValues>;
    return {
      fullName: typeof parsed.fullName === 'string' ? parsed.fullName : '',
      email: typeof parsed.email === 'string' ? parsed.email : '',
      phone: typeof parsed.phone === 'string' ? parsed.phone : '',
    };
  } catch {
    return {};
  }
}

function persistContactProfile(values: ContactFormValues): void {
  if (typeof window === 'undefined') {
    return;
  }
  const payload = {
    fullName: values.fullName,
    email: values.email,
    phone: values.phone,
  };
  window.localStorage.setItem(CONTACT_PROFILE_KEY, JSON.stringify(payload));
}

export function ContactLeadForm({
  content,
  contactPhone,
  className,
}: ContactLeadFormProps): JSX.Element {
  const [lastSubmittedName, setLastSubmittedName] = useState<string | null>(null);
  const [submitSucceeded, setSubmitSucceeded] = useState(false);
  const controlClassName =
    'w-full rounded-xl border border-surface-subtle bg-white px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm';

  const form = useFormController<ContactFormValues>({
    initialValues: {
      ...EMPTY_FORM,
      ...loadContactProfile(),
    },
    schema: CONTACT_FORM_SCHEMA,
    onSubmit: async (values) => {
      const notes = buildContactSubmissionNotes(values);
      const result = await submitLeadFromLanding({
        values: {
          fullName: values.fullName,
          email: values.email,
          phone: values.phone,
          campaignSource: LEAD_CAMPAIGN_SOURCE_CONTACT,
          campaignMedium: LEAD_CAMPAIGN_MEDIUM_CONTACT,
          desiredPropertyId: null,
          desiredUnitId: null,
          budgetMonthly: null,
          preferredMoveInDate: null,
          notes,
        },
      });
      setLastSubmittedName(values.fullName);
      trackLeadSubmission({
        leadId: result.id,
        campaignSource: LEAD_CAMPAIGN_SOURCE_CONTACT,
        campaignMedium: LEAD_CAMPAIGN_MEDIUM_CONTACT,
        stage: 'new',
      });
    },
    mapSubmitError: (error) => ({
      message:
        content.errorMessage.trim().length > 0
          ? content.errorMessage
          : error instanceof Error
            ? error.message
            : 'Unable to submit your message.',
    }),
  });

  useEffect(() => {
    if (form.isDirty) {
      setSubmitSucceeded(false);
    }
  }, [form.isDirty]);

  useEffect(() => {
    persistContactProfile(form.values);
  }, [form.values]);

  const fullNameField = form.field('fullName');
  const phoneField = form.field('phone');
  const emailField = form.field('email');
  const spaceTypeField = form.field('spaceType');
  const targetSizeField = form.field('targetSize');
  const timelineField = form.field('timeline');
  const messageField = form.field('message');

  const hasSubmitError = typeof form.submitError === 'string' && form.submitError.length > 0;
  const showSuccess = submitSucceeded && lastSubmittedName !== null;
  const successMessage = applyTemplate(content.successMessage, {
    name: lastSubmittedName ?? '',
  });
  const helperText = applyTemplate(content.helperText, { phone: contactPhone });
  const phonePlaceholder = applyTemplate(content.fields.phonePlaceholder, { phone: contactPhone });

  return (
    <div
      className={clsx(
        'rounded-3xl border border-surface-subtle bg-white p-6 shadow-card md:p-8',
        className,
      )}
    >
      <div className="mb-6 flex flex-col gap-2">
        <h2 className="text-2xl font-semibold text-brand-primary">{content.title}</h2>
        <p className="text-sm text-text-primary/70">{content.description}</p>
        {showSuccess ? (
          <FormMessage tone="success" role="status">
            {successMessage}
          </FormMessage>
        ) : null}
        {hasSubmitError ? (
          <FormMessage tone="error" role="alert">
            {form.submitError}
          </FormMessage>
        ) : null}
      </div>

      <Form
        gap="lg"
        onSubmit={async (event) => {
          const succeeded = await form.handleSubmit(event);
          if (succeeded) {
            setSubmitSucceeded(true);
            form.reset(EMPTY_FORM);
          }
        }}
      >
        <div className="grid gap-4 md:grid-cols-2">
          <FormField
            id="contact-full-name"
            label={content.fields.fullNameLabel}
            required
            error={form.touched.has('fullName') ? form.errors.fullName : undefined}
          >
            <input
              type="text"
              value={fullNameField.value}
              onChange={fullNameField.onChange}
              onBlur={fullNameField.onBlur}
              className={controlClassName}
              placeholder={content.fields.fullNamePlaceholder}
            />
          </FormField>

          <FormField
            id="contact-phone"
            label={content.fields.phoneLabel}
            required
            error={form.touched.has('phone') ? form.errors.phone : undefined}
          >
            <input
              type="tel"
              value={phoneField.value}
              onChange={phoneField.onChange}
              onBlur={phoneField.onBlur}
              className={controlClassName}
              placeholder={phonePlaceholder}
            />
          </FormField>

          <FormField
            id="contact-email"
            label={content.fields.emailLabel}
            required
            error={form.touched.has('email') ? form.errors.email : undefined}
          >
            <input
              type="email"
              value={emailField.value}
              onChange={emailField.onChange}
              onBlur={emailField.onBlur}
              className={controlClassName}
              placeholder={content.fields.emailPlaceholder}
              autoComplete="email"
            />
          </FormField>

          <FormField id="contact-space-type" label={content.fields.spaceTypeLabel}>
            <select
              value={spaceTypeField.value ?? ''}
              onChange={spaceTypeField.onChange}
              onBlur={spaceTypeField.onBlur}
              className={controlClassName}
            >
              <option value="">{content.fields.spaceTypePlaceholder}</option>
              {content.fields.spaceTypeOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </FormField>

          <FormField id="contact-target-size" label={content.fields.targetSizeLabel}>
            <input
              type="text"
              inputMode="numeric"
              value={targetSizeField.value ?? ''}
              onChange={targetSizeField.onChange}
              onBlur={targetSizeField.onBlur}
              className={controlClassName}
              placeholder={content.fields.targetSizePlaceholder}
            />
          </FormField>

          <FormField id="contact-timeline" label={content.fields.timelineLabel}>
            <select
              value={timelineField.value ?? ''}
              onChange={timelineField.onChange}
              onBlur={timelineField.onBlur}
              className={controlClassName}
            >
              <option value="">{content.fields.timelinePlaceholder}</option>
              {content.fields.timelineOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </FormField>

          <FormField
            id="contact-message"
            label={content.fields.messageLabel}
            required
            error={form.touched.has('message') ? form.errors.message : undefined}
            className="md:col-span-2"
          >
            <textarea
              value={messageField.value}
              onChange={messageField.onChange}
              onBlur={messageField.onBlur}
              className={`${controlClassName} min-h-[140px]`}
              placeholder={content.fields.messagePlaceholder}
            />
          </FormField>
        </div>

        <FormActions justify="between">
          <div className="flex flex-col gap-1 text-sm text-text-primary/70">
            {helperText.length > 0 ? <span>{helperText}</span> : null}
          </div>
          <PillButton
            type="submit"
            variant="primary"
            disabled={form.isSubmitting}
            className="min-w-[8rem] justify-center"
          >
            {form.isSubmitting ? 'Sending…' : content.submitLabel}
          </PillButton>
        </FormActions>
      </Form>
    </div>
  );
}
